import { html, nothing } from "lit";
import type {
  AgentIdentityResult,
  AgentsFilesListResult,
  AgentsListResult,
  ChannelsStatusSnapshot,
  CronJob,
  CronStatus,
  SkillStatusReport,
} from "../types.ts";
import { t } from "../i18n.ts";
import {
  renderAgentFiles,
  renderAgentChannels,
  renderAgentCron,
} from "./agents-panels-status-files.ts";
import { renderAgentTools, renderAgentSkills } from "./agents-panels-tools-skills.ts";
import type { ModelChoice } from "../controllers/model-catalog.ts";
import type { SubagentsStatusResult } from "../controllers/subagents-status.ts";
import type { QueueStatusResult } from "../controllers/queue-status.ts";
import {
  agentBadgeText,
  buildAgentContext,
  buildAvailableForFallback,
  buildCatalogEntries,
  buildModelOptions,
  normalizeAgentLabel,
  normalizeModelValue,
  resolveAgentConfig,
  resolveAgentEmoji,
  resolveCustomEnvVars,
  resolveModelFallbacks,
  resolveModelLabel,
  resolveModelPrimary,
} from "./agents-utils.ts";

export type AgentsPanel =
  | "overview"
  | "files"
  | "tools"
  | "skills"
  | "channels"
  | "cron"
  | "runtime";

export type AgentsProps = {
  loading: boolean;
  error: string | null;
  agentsList: AgentsListResult | null;
  selectedAgentId: string | null;
  activePanel: AgentsPanel;
  configForm: Record<string, unknown> | null;
  configLoading: boolean;
  configSaving: boolean;
  configDirty: boolean;
  channelsLoading: boolean;
  channelsError: string | null;
  channelsSnapshot: ChannelsStatusSnapshot | null;
  channelsLastSuccess: number | null;
  cronLoading: boolean;
  cronStatus: CronStatus | null;
  cronJobs: CronJob[];
  cronError: string | null;
  agentFilesLoading: boolean;
  agentFilesError: string | null;
  agentFilesList: AgentsFilesListResult | null;
  agentFileActive: string | null;
  agentFileContents: Record<string, string>;
  agentFileDrafts: Record<string, string>;
  agentFileSaving: boolean;
  agentIdentityLoading: boolean;
  agentIdentityError: string | null;
  agentIdentityById: Record<string, AgentIdentityResult>;
  agentSkillsLoading: boolean;
  agentSkillsReport: SkillStatusReport | null;
  agentSkillsError: string | null;
  agentSkillsAgentId: string | null;
  skillsFilter: string;
  modelCatalogAll: ModelChoice[];
  modelCatalogLoading: boolean;
  onLoadModelCatalog: () => void;
  onRefresh: () => void;
  onSelectAgent: (agentId: string) => void;
  onSelectPanel: (panel: AgentsPanel) => void;
  onLoadFiles: (agentId: string) => void;
  onSelectFile: (name: string) => void;
  onFileDraftChange: (name: string, content: string) => void;
  onFileReset: (name: string) => void;
  onFileSave: (name: string) => void;
  onToolsProfileChange: (agentId: string, profile: string | null, clearAllow: boolean) => void;
  onToolsOverridesChange: (agentId: string, alsoAllow: string[], deny: string[]) => void;
  onConfigReload: () => void;
  onConfigSave: () => void;
  onModelChange: (agentId: string, modelId: string | null) => void;
  onModelFallbacksChange: (agentId: string, fallbacks: string[]) => void;
  onApiKeyChange: (envVar: string, value: string) => void;
  onCatalogAdd: (modelId: string, alias?: string) => void;
  onCatalogRemove: (modelId: string) => void;
  onCatalogEditAlias: (modelId: string, alias: string) => void;
  onFallbackAdd: (agentId: string, modelId: string) => void;
  onFallbackRemove: (agentId: string, index: number) => void;
  onCustomEnvVarAdd: (key: string, value: string) => void;
  onCustomEnvVarRemove: (key: string) => void;
  onChannelsRefresh: () => void;
  onCronRefresh: () => void;
  onSkillsFilterChange: (next: string) => void;
  onSkillsRefresh: () => void;
  onAgentSkillToggle: (agentId: string, skillName: string, enabled: boolean) => void;
  onAgentSkillsClear: (agentId: string) => void;
  onAgentSkillsDisableAll: (agentId: string) => void;
  // Runtime tab
  subagentsStatus: SubagentsStatusResult | null;
  subagentsStatusLoading: boolean;
  queueStatus: QueueStatusResult | null;
  queueStatusLoading: boolean;
  onRuntimeRefresh: () => void;
};

export type AgentContext = {
  workspace: string;
  model: string;
  identityName: string;
  identityEmoji: string;
  skillsLabel: string;
  isDefault: boolean;
};

export function renderAgents(props: AgentsProps) {
  const agents = props.agentsList?.agents ?? [];
  const defaultId = props.agentsList?.defaultId ?? null;
  const selectedId = props.selectedAgentId ?? defaultId ?? agents[0]?.id ?? null;
  const selectedAgent = selectedId
    ? (agents.find((agent) => agent.id === selectedId) ?? null)
    : null;

  return html`
    <div class="agents-layout">
      <section class="card agents-sidebar">
        <div class="row" style="justify-content: space-between;">
          <div>
            <div class="card-title">${t("agents.title")}</div>
            <div class="card-sub">${agents.length} ${t("agents.configured")}.</div>
          </div>
          <button class="btn btn--sm" ?disabled=${props.loading} @click=${props.onRefresh}>
            ${props.loading ? t("common.loading") : t("common.refresh")}
          </button>
        </div>
        ${
          props.error
            ? html`<div class="callout danger" style="margin-top: 12px;">${props.error}</div>`
            : nothing
        }
        <div class="agent-list" style="margin-top: 12px;">
          ${
            agents.length === 0
              ? html`
                  <div class="muted">${t("agents.no_agents")}</div>
                `
              : agents.map((agent) => {
                  const badge = agentBadgeText(agent.id, defaultId);
                  const emoji = resolveAgentEmoji(agent, props.agentIdentityById[agent.id] ?? null);
                  return html`
                    <button
                      type="button"
                      class="agent-row ${selectedId === agent.id ? "active" : ""}"
                      @click=${() => props.onSelectAgent(agent.id)}
                    >
                      <div class="agent-avatar">${emoji || normalizeAgentLabel(agent).slice(0, 1)}</div>
                      <div class="agent-info">
                        <div class="agent-title">${normalizeAgentLabel(agent)}</div>
                        <div class="agent-sub mono">${agent.id}</div>
                      </div>
                      ${badge ? html`<span class="agent-pill">${badge}</span>` : nothing}
                    </button>
                  `;
                })
          }
        </div>
      </section>
      <section class="agents-main">
        ${
          !selectedAgent
            ? html`
                <div class="card">
                  <div class="card-title">${t("agents.select")}</div>
                  <div class="card-sub">${t("agents.select_sub")}</div>
                </div>
              `
            : html`
                ${renderAgentHeader(
                  selectedAgent,
                  defaultId,
                  props.agentIdentityById[selectedAgent.id] ?? null,
                )}
                ${renderAgentTabs(props.activePanel, (panel) => props.onSelectPanel(panel))}
                ${
                  props.activePanel === "overview"
                    ? renderAgentOverview({
                        agent: selectedAgent,
                        defaultId,
                        configForm: props.configForm,
                        agentFilesList: props.agentFilesList,
                        agentIdentity: props.agentIdentityById[selectedAgent.id] ?? null,
                        agentIdentityError: props.agentIdentityError,
                        agentIdentityLoading: props.agentIdentityLoading,
                        configLoading: props.configLoading,
                        configSaving: props.configSaving,
                        configDirty: props.configDirty,
                        modelCatalogAll: props.modelCatalogAll,
                        modelCatalogLoading: props.modelCatalogLoading,
                        onLoadModelCatalog: props.onLoadModelCatalog,
                        onConfigReload: props.onConfigReload,
                        onConfigSave: props.onConfigSave,
                        onModelChange: props.onModelChange,
                        onApiKeyChange: props.onApiKeyChange,
                        onCatalogAdd: props.onCatalogAdd,
                        onCatalogRemove: props.onCatalogRemove,
                        onCatalogEditAlias: props.onCatalogEditAlias,
                        onFallbackAdd: props.onFallbackAdd,
                        onFallbackRemove: props.onFallbackRemove,
                        onCustomEnvVarAdd: props.onCustomEnvVarAdd,
                        onCustomEnvVarRemove: props.onCustomEnvVarRemove,
                      })
                    : nothing
                }
                ${
                  props.activePanel === "files"
                    ? renderAgentFiles({
                        agentId: selectedAgent.id,
                        agentFilesList: props.agentFilesList,
                        agentFilesLoading: props.agentFilesLoading,
                        agentFilesError: props.agentFilesError,
                        agentFileActive: props.agentFileActive,
                        agentFileContents: props.agentFileContents,
                        agentFileDrafts: props.agentFileDrafts,
                        agentFileSaving: props.agentFileSaving,
                        onLoadFiles: props.onLoadFiles,
                        onSelectFile: props.onSelectFile,
                        onFileDraftChange: props.onFileDraftChange,
                        onFileReset: props.onFileReset,
                        onFileSave: props.onFileSave,
                      })
                    : nothing
                }
                ${
                  props.activePanel === "tools"
                    ? renderAgentTools({
                        agentId: selectedAgent.id,
                        configForm: props.configForm,
                        configLoading: props.configLoading,
                        configSaving: props.configSaving,
                        configDirty: props.configDirty,
                        onProfileChange: props.onToolsProfileChange,
                        onOverridesChange: props.onToolsOverridesChange,
                        onConfigReload: props.onConfigReload,
                        onConfigSave: props.onConfigSave,
                      })
                    : nothing
                }
                ${
                  props.activePanel === "skills"
                    ? renderAgentSkills({
                        agentId: selectedAgent.id,
                        report: props.agentSkillsReport,
                        loading: props.agentSkillsLoading,
                        error: props.agentSkillsError,
                        activeAgentId: props.agentSkillsAgentId,
                        configForm: props.configForm,
                        configLoading: props.configLoading,
                        configSaving: props.configSaving,
                        configDirty: props.configDirty,
                        filter: props.skillsFilter,
                        onFilterChange: props.onSkillsFilterChange,
                        onRefresh: props.onSkillsRefresh,
                        onToggle: props.onAgentSkillToggle,
                        onClear: props.onAgentSkillsClear,
                        onDisableAll: props.onAgentSkillsDisableAll,
                        onConfigReload: props.onConfigReload,
                        onConfigSave: props.onConfigSave,
                      })
                    : nothing
                }
                ${
                  props.activePanel === "channels"
                    ? renderAgentChannels({
                        context: buildAgentContext(
                          selectedAgent,
                          props.configForm,
                          props.agentFilesList,
                          defaultId,
                          props.agentIdentityById[selectedAgent.id] ?? null,
                        ),
                        configForm: props.configForm,
                        snapshot: props.channelsSnapshot,
                        loading: props.channelsLoading,
                        error: props.channelsError,
                        lastSuccess: props.channelsLastSuccess,
                        onRefresh: props.onChannelsRefresh,
                      })
                    : nothing
                }
                ${
                  props.activePanel === "cron"
                    ? renderAgentCron({
                        context: buildAgentContext(
                          selectedAgent,
                          props.configForm,
                          props.agentFilesList,
                          defaultId,
                          props.agentIdentityById[selectedAgent.id] ?? null,
                        ),
                        agentId: selectedAgent.id,
                        jobs: props.cronJobs,
                        status: props.cronStatus,
                        loading: props.cronLoading,
                        error: props.cronError,
                        onRefresh: props.onCronRefresh,
                      })
                    : nothing
                }
                ${
                  props.activePanel === "runtime"
                    ? renderAgentRuntime({
                        subagentsStatus: props.subagentsStatus,
                        subagentsStatusLoading: props.subagentsStatusLoading,
                        queueStatus: props.queueStatus,
                        queueStatusLoading: props.queueStatusLoading,
                        onRefresh: props.onRuntimeRefresh,
                      })
                    : nothing
                }
              `
        }
      </section>
    </div>
  `;
}

function renderAgentHeader(
  agent: AgentsListResult["agents"][number],
  defaultId: string | null,
  agentIdentity: AgentIdentityResult | null,
) {
  const badge = agentBadgeText(agent.id, defaultId);
  const displayName = normalizeAgentLabel(agent);
  const subtitle = agent.identity?.theme?.trim() || "Agent workspace and routing.";
  const emoji = resolveAgentEmoji(agent, agentIdentity);
  return html`
    <section class="card agent-header">
      <div class="agent-header-main">
        <div class="agent-avatar agent-avatar--lg">${emoji || displayName.slice(0, 1)}</div>
        <div>
          <div class="card-title">${displayName}</div>
          <div class="card-sub">${subtitle}</div>
        </div>
      </div>
      <div class="agent-header-meta">
        <div class="mono">${agent.id}</div>
        ${badge ? html`<span class="agent-pill">${badge}</span>` : nothing}
      </div>
    </section>
  `;
}

function renderAgentTabs(active: AgentsPanel, onSelect: (panel: AgentsPanel) => void) {
  const tabs: Array<{ id: AgentsPanel; label: string }> = [
    { id: "overview", label: t("agents.tab_overview") },
    { id: "runtime", label: t("agents.tab_runtime") },
    { id: "files", label: t("agents.tab_files") },
    { id: "tools", label: t("agents.tab_tools") },
    { id: "skills", label: t("agents.tab_skills") },
    { id: "channels", label: t("agents.tab_channels") },
    { id: "cron", label: t("agents.tab_cron") },
  ];
  return html`
    <div class="agent-tabs">
      ${tabs.map(
        (tab) => html`
          <button
            class="agent-tab ${active === tab.id ? "active" : ""}"
            type="button"
            @click=${() => onSelect(tab.id)}
          >
            ${tab.label}
          </button>
        `,
      )}
    </div>
  `;
}

/** Sentinel used by the server to redact sensitive values. */
const REDACTED_SENTINEL = "__NABE_REDACTED__";

/** Well-known LLM provider API key env var mappings. */
const PROVIDER_API_KEYS = [
  { envVar: "ANTHROPIC_API_KEY", label: "Anthropic (Claude)", provider: "anthropic" },
  { envVar: "OPENAI_API_KEY", label: "OpenAI (GPT / o-series)", provider: "openai" },
  { envVar: "GOOGLE_API_KEY", label: "Google (Gemini)", provider: "google" },
  { envVar: "GROQ_API_KEY", label: "Groq", provider: "groq" },
  { envVar: "OPENROUTER_API_KEY", label: "OpenRouter", provider: "openrouter" },
  { envVar: "MISTRAL_API_KEY", label: "Mistral", provider: "mistral" },
] as const;

function renderAgentOverview(params: {
  agent: AgentsListResult["agents"][number];
  defaultId: string | null;
  configForm: Record<string, unknown> | null;
  agentFilesList: AgentsFilesListResult | null;
  agentIdentity: AgentIdentityResult | null;
  agentIdentityLoading: boolean;
  agentIdentityError: string | null;
  configLoading: boolean;
  configSaving: boolean;
  configDirty: boolean;
  modelCatalogAll: ModelChoice[];
  modelCatalogLoading: boolean;
  onLoadModelCatalog: () => void;
  onConfigReload: () => void;
  onConfigSave: () => void;
  onModelChange: (agentId: string, modelId: string | null) => void;
  onApiKeyChange: (envVar: string, value: string) => void;
  onCatalogAdd: (modelId: string, alias?: string) => void;
  onCatalogRemove: (modelId: string) => void;
  onCatalogEditAlias: (modelId: string, alias: string) => void;
  onFallbackAdd: (agentId: string, modelId: string) => void;
  onFallbackRemove: (agentId: string, index: number) => void;
  onCustomEnvVarAdd: (key: string, value: string) => void;
  onCustomEnvVarRemove: (key: string) => void;
}) {
  const {
    agent,
    configForm,
    agentFilesList,
    agentIdentity,
    agentIdentityLoading,
    agentIdentityError,
    configLoading,
    configSaving,
    configDirty,
    modelCatalogAll,
    modelCatalogLoading,
    onLoadModelCatalog,
    onConfigReload,
    onConfigSave,
    onModelChange,
    onApiKeyChange,
    onCatalogAdd,
    onCatalogRemove,
    onCatalogEditAlias,
    onFallbackAdd,
    onFallbackRemove,
    onCustomEnvVarAdd,
    onCustomEnvVarRemove,
  } = params;
  const config = resolveAgentConfig(configForm, agent.id);
  const workspaceFromFiles =
    agentFilesList && agentFilesList.agentId === agent.id ? agentFilesList.workspace : null;
  const workspace =
    workspaceFromFiles || config.entry?.workspace || config.defaults?.workspace || "default";
  const model = config.entry?.model
    ? resolveModelLabel(config.entry?.model)
    : resolveModelLabel(config.defaults?.model);
  const defaultModel = resolveModelLabel(config.defaults?.model);
  const modelPrimary =
    resolveModelPrimary(config.entry?.model) || (model !== "-" ? normalizeModelValue(model) : null);
  const defaultPrimary =
    resolveModelPrimary(config.defaults?.model) ||
    (defaultModel !== "-" ? normalizeModelValue(defaultModel) : null);
  const effectivePrimary = modelPrimary ?? defaultPrimary ?? null;
  const modelFallbacks = resolveModelFallbacks(config.entry?.model) ?? [];
  const identityName =
    agentIdentity?.name?.trim() ||
    agent.identity?.name?.trim() ||
    agent.name?.trim() ||
    config.entry?.name ||
    "-";
  const resolvedEmoji = resolveAgentEmoji(agent, agentIdentity);
  const identityEmoji = resolvedEmoji || "-";
  const skillFilter = Array.isArray(config.entry?.skills) ? config.entry?.skills : null;
  const skillCount = skillFilter?.length ?? null;
  const identityStatus = agentIdentityLoading
    ? t("common.loading")
    : agentIdentityError
      ? "Unavailable"
      : "";
  const isDefault = Boolean(params.defaultId && agent.id === params.defaultId);
  const disabled = !configForm || configLoading || configSaving;

  return html`
    <!-- Overview card -->
    <section class="card">
      <div class="card-title">${t("agents.tab_overview")}</div>
      <div class="card-sub">${t("agents.overview_sub")}</div>
      <div class="agents-overview-grid" style="margin-top: 16px;">
        <div class="agent-kv">
          <div class="label">${t("agents.workspace")}</div>
          <div class="mono">${workspace}</div>
        </div>
        <div class="agent-kv">
          <div class="label">${t("agents.primary_model")}</div>
          <div class="mono">${model}</div>
        </div>
        <div class="agent-kv">
          <div class="label">${t("agents.identity_name")}</div>
          <div>${identityName}</div>
          ${identityStatus ? html`<div class="agent-kv-sub muted">${identityStatus}</div>` : nothing}
        </div>
        <div class="agent-kv">
          <div class="label">${t("agents.default")}</div>
          <div>${isDefault ? t("common.yes") : t("common.no")}</div>
        </div>
        <div class="agent-kv">
          <div class="label">${t("agents.identity_emoji")}</div>
          <div>${identityEmoji}</div>
        </div>
        <div class="agent-kv">
          <div class="label">${t("agents.skills_filter")}</div>
          <div>${skillFilter ? `${skillCount} ${t("agents.selected")}` : t("agents.all_skills")}</div>
        </div>
      </div>
    </section>

    <!-- Section A: Model Catalog + API Keys -->
    ${renderModelCatalog({
      configForm,
      disabled,
      effectivePrimary,
      modelFallbacks,
      modelCatalogAll,
      modelCatalogLoading,
      onLoadModelCatalog,
      onCatalogAdd,
      onCatalogRemove,
      onCatalogEditAlias,
      onApiKeyChange,
      onCustomEnvVarAdd,
      onCustomEnvVarRemove,
    })}

    <!-- Section B: Primary + Fallbacks -->
    ${renderModelSelection({
      agent,
      configForm,
      disabled,
      isDefault,
      effectivePrimary,
      defaultPrimary,
      modelFallbacks,
      configLoading,
      configSaving,
      configDirty,
      onConfigReload,
      onConfigSave,
      onModelChange,
      onFallbackAdd,
      onFallbackRemove,
    })}
  `;
}

/* ─── Section A: Model Catalog ─────────────────────────────────────── */

/** Build a provider -> models map from the full catalog. */
function groupByProvider(models: ModelChoice[]): Map<string, ModelChoice[]> {
  const map = new Map<string, ModelChoice[]>();
  for (const m of models) {
    const list = map.get(m.provider) ?? [];
    list.push(m);
    map.set(m.provider, list);
  }
  return map;
}

/**
 * Rebuild the model <select> options based on the chosen provider.
 * Uses direct DOM manipulation so we don't need reactive module-level state.
 */
function rebuildModelSelect(
  row: Element,
  providers: Map<string, ModelChoice[]>,
  selectedProvider: string,
) {
  const modelSelect = row.querySelector<HTMLSelectElement>("[data-catalog-model]");
  if (!modelSelect) return;

  const models = providers.get(selectedProvider) ?? [];
  // Clear existing options, keep placeholder
  modelSelect.innerHTML = "";
  const placeholder = document.createElement("option");
  placeholder.value = "";
  placeholder.textContent = modelSelect.dataset.placeholder ?? "Select model…";
  modelSelect.appendChild(placeholder);

  for (const m of models) {
    const opt = document.createElement("option");
    opt.value = m.id;
    opt.textContent = m.name || m.id;
    modelSelect.appendChild(opt);
  }
  modelSelect.value = "";
  modelSelect.disabled = !selectedProvider;

  // Also reset alias + disable add button
  const aliasInput = row.querySelector<HTMLInputElement>("[data-catalog-alias]");
  if (aliasInput) {
    aliasInput.value = "";
    aliasInput.disabled = true;
  }
  const addBtn = row.querySelector<HTMLButtonElement>("[data-catalog-add-btn]");
  if (addBtn) addBtn.disabled = true;
}

function renderModelCatalog(params: {
  configForm: Record<string, unknown> | null;
  disabled: boolean;
  effectivePrimary: string | null;
  modelFallbacks: string[];
  modelCatalogAll: ModelChoice[];
  modelCatalogLoading: boolean;
  onLoadModelCatalog: () => void;
  onCatalogAdd: (modelId: string, alias?: string) => void;
  onCatalogRemove: (modelId: string) => void;
  onCatalogEditAlias: (modelId: string, alias: string) => void;
  onApiKeyChange: (envVar: string, value: string) => void;
  onCustomEnvVarAdd: (key: string, value: string) => void;
  onCustomEnvVarRemove: (key: string) => void;
}) {
  const {
    configForm,
    disabled,
    effectivePrimary,
    modelFallbacks,
    modelCatalogAll,
    modelCatalogLoading,
    onLoadModelCatalog,
    onCatalogAdd,
    onCatalogRemove,
    onCatalogEditAlias,
    onApiKeyChange,
    onCustomEnvVarAdd,
    onCustomEnvVarRemove,
  } = params;
  const catalog = buildCatalogEntries(configForm);
  const providers = groupByProvider(modelCatalogAll);
  const providerNames = [...providers.keys()].sort();

  // Build a model-id → provider lookup from the full catalog
  const modelProviderMap = new Map<string, string>();
  for (const m of modelCatalogAll) {
    modelProviderMap.set(m.id, m.provider);
  }

  // Auto-load model catalog on first render if empty and not loading
  if (modelCatalogAll.length === 0 && !modelCatalogLoading) {
    onLoadModelCatalog();
  }

  const handleProviderChange = (e: Event) => {
    const select = e.target as HTMLSelectElement;
    const row = select.closest(".model-add-row");
    if (row) rebuildModelSelect(row, providers, select.value);
  };

  const handleModelChange = (e: Event) => {
    const select = e.target as HTMLSelectElement;
    const row = select.closest(".model-add-row");
    if (!row) return;
    const hasModel = !!select.value;
    const aliasInput = row.querySelector<HTMLInputElement>("[data-catalog-alias]");
    if (aliasInput) aliasInput.disabled = !hasModel;
    const addBtn = row.querySelector<HTMLButtonElement>("[data-catalog-add-btn]");
    if (addBtn) addBtn.disabled = !hasModel;
  };

  const handleAddClick = (e: Event) => {
    const row = (e.target as HTMLElement).closest(".model-add-row");
    if (!row) return;
    const modelSelect = row.querySelector<HTMLSelectElement>("[data-catalog-model]");
    const aliasInput = row.querySelector<HTMLInputElement>("[data-catalog-alias]");
    const providerSelect = row.querySelector<HTMLSelectElement>("[data-catalog-provider]");
    const modelId = modelSelect?.value?.trim();
    if (!modelId) return;
    const alias = aliasInput?.value?.trim() || undefined;
    onCatalogAdd(modelId, alias);
    // Reset the row
    if (providerSelect) providerSelect.value = "";
    if (modelSelect) {
      modelSelect.innerHTML = "";
      const ph = document.createElement("option");
      ph.value = "";
      ph.textContent = modelSelect.dataset.placeholder ?? "";
      modelSelect.appendChild(ph);
      modelSelect.disabled = true;
    }
    if (aliasInput) {
      aliasInput.value = "";
      aliasInput.disabled = true;
    }
    const addBtn = row.querySelector<HTMLButtonElement>("[data-catalog-add-btn]");
    if (addBtn) addBtn.disabled = true;
  };

  /** Resolve provider for a catalog model via full catalog lookup or ID prefix. */
  const resolveProvider = (modelId: string): string | null => {
    const fromCatalog = modelProviderMap.get(modelId);
    if (fromCatalog) return fromCatalog;
    const slash = modelId.indexOf("/");
    if (slash > 0) return modelId.slice(0, slash);
    return null;
  };

  /** Find the PROVIDER_API_KEYS entry for a given provider name. */
  const findProviderKey = (provider: string) =>
    PROVIDER_API_KEYS.find((pk) => pk.provider === provider) ?? null;

  // Custom env vars (exclude well-known provider keys)
  const customVars = resolveCustomEnvVars(configForm, KNOWN_ENV_KEYS);
  let newEnvKey = "";
  let newEnvValue = "";

  // Track which provider keys we've already rendered to avoid duplicate inputs
  const renderedProviderKeys = new Set<string>();

  return html`
    <section class="card" style="margin-top: 16px;">
      <div class="card-title">${t("agents.catalog_title")}</div>
      <div class="card-sub">${t("agents.catalog_sub")}</div>

      <div class="model-catalog-table" style="margin-top: 14px;">
        ${
          catalog.length === 0
            ? html`<div class="muted" style="font-size: 13px; padding: 8px 0;">${t("agents.catalog_empty")}</div>`
            : html`
                <div class="model-catalog-head">
                  <span>${t("agents.catalog_model_id")}</span>
                  <span>${t("agents.catalog_alias")}</span>
                  <span>${t("agents.api_key_label")}</span>
                  <span>${t("agents.catalog_actions")}</span>
                </div>
                ${catalog.map((entry) => {
                  const provider = resolveProvider(entry.modelId);
                  const pk = provider ? findProviderKey(provider) : null;
                  const isDuplicate = pk ? renderedProviderKeys.has(pk.envVar) : false;
                  if (pk) renderedProviderKeys.add(pk.envVar);
                  const currentValue = pk ? resolveEnvVarValue(configForm, pk.envVar) : "";
                  const isRedacted = currentValue === REDACTED_SENTINEL;
                  const isSet = isRedacted || currentValue.length > 0;
                  return html`
                    <div class="model-catalog-row">
                      <span class="mono" style="font-size: 12px;">${entry.modelId}</span>
                      <span>
                        <input
                          class="catalog-alias-input"
                          .value=${entry.alias}
                          placeholder=${t("agents.catalog_alias_placeholder")}
                          ?disabled=${disabled}
                          @change=${(e: Event) =>
                            onCatalogEditAlias(
                              entry.modelId,
                              (e.target as HTMLInputElement).value.trim(),
                            )}
                        />
                      </span>
                      <span>
                        ${
                          pk
                            ? isDuplicate
                              ? html`<span class="muted" style="font-size: 11px;">${isSet ? html`&#x2713; ${pk.label}` : pk.label}</span>`
                              : html`
                                <span style="display: flex; align-items: center; gap: 4px;">
                                  <input
                                    type="password"
                                    class="catalog-alias-input"
                                    .value=${isRedacted ? "" : currentValue}
                                    placeholder=${isRedacted ? `${pk.label} ✓` : pk.label}
                                    ?disabled=${disabled}
                                    @input=${(e: Event) =>
                                      onApiKeyChange(
                                        pk.envVar,
                                        (e.target as HTMLInputElement).value,
                                      )}
                                  />
                                  ${
                                    isSet && isRedacted
                                      ? html`
                                          <span style="font-size: 11px; color: var(--color-success, #4ade80)">&#x2713;</span>
                                        `
                                      : nothing
                                  }
                                </span>
                              `
                            : html`
                                <span class="muted" style="font-size: 11px">—</span>
                              `
                        }
                      </span>
                      <span>
                        <button
                          class="btn btn--sm danger"
                          ?disabled=${disabled}
                          @click=${() => {
                            const inUse =
                              effectivePrimary === entry.modelId ||
                              modelFallbacks.includes(entry.modelId);
                            if (inUse && !confirm(t("agents.catalog_confirm_remove"))) return;
                            onCatalogRemove(entry.modelId);
                          }}
                        >
                          &times;
                        </button>
                      </span>
                    </div>
                  `;
                })}
              `
        }

        <!-- Add model: provider-first cascading dropdowns -->
        <div class="model-add-row" style="margin-top: 10px;">
          ${
            modelCatalogLoading
              ? html`<span class="muted" style="font-size: 13px;">${t("common.loading")}</span>`
              : providerNames.length === 0
                ? html`<span class="muted" style="font-size: 13px;">${t("agents.catalog_no_providers")}</span>`
                : html`
                    <!-- Provider dropdown -->
                    <select
                      data-catalog-provider
                      class="model-add-input"
                      ?disabled=${disabled}
                      @change=${handleProviderChange}
                    >
                      <option value="">${t("agents.catalog_select_provider")}</option>
                      ${providerNames.map((p) => html`<option value=${p}>${p}</option>`)}
                    </select>

                    <!-- Model dropdown (filtered by provider) -->
                    <select
                      data-catalog-model
                      data-placeholder=${t("agents.catalog_select_model")}
                      class="model-add-input"
                      disabled
                      @change=${handleModelChange}
                    >
                      <option value="">${t("agents.catalog_select_model")}</option>
                    </select>

                    <!-- Alias input -->
                    <input
                      data-catalog-alias
                      class="model-add-alias"
                      placeholder=${t("agents.catalog_alias_placeholder")}
                      disabled
                      @keydown=${(e: KeyboardEvent) => {
                        if (e.key === "Enter") handleAddClick(e);
                      }}
                    />

                    <!-- Add button -->
                    <button
                      data-catalog-add-btn
                      class="btn btn--sm primary"
                      disabled
                      @click=${handleAddClick}
                    >
                      ${t("agents.catalog_add")}
                    </button>
                  `
          }
        </div>
      </div>

      <!-- Custom environment variables -->
      <div style="margin-top: 20px;">
        <div class="label">${t("agents.custom_env_title")}</div>
        <div class="card-sub" style="margin-top: 2px;">${t("agents.custom_env_sub")}</div>

        <div class="custom-env-list" style="margin-top: 10px;">
          ${customVars.map((cv) => {
            const isRedacted = cv.value === REDACTED_SENTINEL;
            const isSet = isRedacted || cv.value.length > 0;
            return html`
              <div class="custom-env-row">
                <span class="mono custom-env-key">${cv.key}</span>
                <input
                  type="password"
                  class="custom-env-value"
                  .value=${isRedacted ? "" : cv.value}
                  placeholder=${isRedacted ? t("agents.api_key_set") : t("agents.custom_env_value_placeholder")}
                  ?disabled=${disabled}
                  @input=${(e: Event) =>
                    onApiKeyChange(cv.key, (e.target as HTMLInputElement).value)}
                />
                ${
                  isSet && isRedacted
                    ? html`
                        <span style="font-size: 11px; color: var(--color-success, #4ade80); white-space: nowrap"
                          >&#x2713;</span
                        >
                      `
                    : nothing
                }
                <button
                  class="fallback-remove"
                  ?disabled=${disabled}
                  title="Remove"
                  @click=${() => onCustomEnvVarRemove(cv.key)}
                >
                  &times;
                </button>
              </div>
            `;
          })}

          <!-- Add new env var -->
          <div class="model-add-row" style="margin-top: 8px;">
            <input
              class="model-add-input"
              placeholder=${t("agents.custom_env_name_placeholder")}
              style="text-transform: uppercase;"
              ?disabled=${disabled}
              @input=${(e: Event) => {
                newEnvKey = (e.target as HTMLInputElement).value;
              }}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key === "Enter") {
                  const trimmedKey = newEnvKey.trim().toUpperCase();
                  if (trimmedKey) {
                    onCustomEnvVarAdd(trimmedKey, newEnvValue);
                    (e.target as HTMLInputElement).value = "";
                    newEnvKey = "";
                    newEnvValue = "";
                  }
                }
              }}
            />
            <input
              class="model-add-alias"
              type="password"
              placeholder=${t("agents.custom_env_value_placeholder")}
              ?disabled=${disabled}
              @input=${(e: Event) => {
                newEnvValue = (e.target as HTMLInputElement).value;
              }}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key === "Enter") {
                  const trimmedKey = newEnvKey.trim().toUpperCase();
                  if (trimmedKey) {
                    onCustomEnvVarAdd(trimmedKey, newEnvValue);
                    const row = (e.target as HTMLInputElement).closest(".model-add-row");
                    if (row) {
                      for (const input of row.querySelectorAll<HTMLInputElement>("input")) {
                        input.value = "";
                      }
                    }
                    newEnvKey = "";
                    newEnvValue = "";
                  }
                }
              }}
            />
            <button
              class="btn btn--sm primary"
              ?disabled=${disabled}
              @click=${(e: Event) => {
                const trimmedKey = newEnvKey.trim().toUpperCase();
                if (!trimmedKey) return;
                onCustomEnvVarAdd(trimmedKey, newEnvValue);
                const row = (e.target as HTMLElement).closest(".model-add-row");
                if (row) {
                  for (const input of row.querySelectorAll<HTMLInputElement>("input")) {
                    input.value = "";
                  }
                }
                newEnvKey = "";
                newEnvValue = "";
              }}
            >
              ${t("agents.custom_env_add")}
            </button>
          </div>
        </div>
      </div>
    </section>
  `;
}

/* ─── Section B: Primary + Fallbacks ───────────────────────────────── */

function renderModelSelection(params: {
  agent: AgentsListResult["agents"][number];
  configForm: Record<string, unknown> | null;
  disabled: boolean;
  isDefault: boolean;
  effectivePrimary: string | null;
  defaultPrimary: string | null;
  modelFallbacks: string[];
  configLoading: boolean;
  configSaving: boolean;
  configDirty: boolean;
  onConfigReload: () => void;
  onConfigSave: () => void;
  onModelChange: (agentId: string, modelId: string | null) => void;
  onFallbackAdd: (agentId: string, modelId: string) => void;
  onFallbackRemove: (agentId: string, index: number) => void;
}) {
  const {
    agent,
    configForm,
    disabled,
    isDefault,
    effectivePrimary,
    defaultPrimary,
    modelFallbacks,
    configLoading,
    configSaving,
    configDirty,
    onConfigReload,
    onConfigSave,
    onModelChange,
    onFallbackAdd,
    onFallbackRemove,
  } = params;

  const available = buildAvailableForFallback(configForm, effectivePrimary, modelFallbacks);

  return html`
    <section class="card" style="margin-top: 16px;">
      <div class="card-title">${t("agents.model_selection")}</div>

      <!-- Primary model dropdown -->
      <div style="margin-top: 14px;">
        <label class="field" style="max-width: 480px;">
          <span>${t("agents.primary_default")}${isDefault ? ` (${t("agents.default")})` : ""}</span>
          <select
            .value=${effectivePrimary ?? ""}
            ?disabled=${disabled}
            @change=${(e: Event) =>
              onModelChange(agent.id, (e.target as HTMLSelectElement).value || null)}
          >
            ${
              isDefault
                ? nothing
                : html`
                    <option value="">
                      ${defaultPrimary ? `${t("agents.inherit_default")} (${defaultPrimary})` : t("agents.inherit_default")}
                    </option>
                  `
            }
            ${buildModelOptions(configForm, effectivePrimary ?? undefined)}
          </select>
        </label>
      </div>

      <!-- Fallback list -->
      <div style="margin-top: 18px;">
        <div class="label">${t("agents.fallback_title")}</div>
        <div class="card-sub" style="margin-top: 2px;">${t("agents.fallback_sub")}</div>

        <div class="fallback-list" style="margin-top: 10px;">
          ${
            modelFallbacks.length === 0
              ? html`<div class="muted" style="font-size: 13px; padding: 4px 0;">${t("agents.fallback_empty")}</div>`
              : modelFallbacks.map(
                  (fb, i) => html`
                    <div class="fallback-item">
                      <span class="fallback-index">${i + 1}.</span>
                      <span class="mono fallback-model">${fb}</span>
                      <button
                        class="fallback-remove"
                        ?disabled=${disabled}
                        title="Remove"
                        @click=${() => onFallbackRemove(agent.id, i)}
                      >
                        &times;
                      </button>
                    </div>
                  `,
                )
          }

          <!-- Add fallback dropdown -->
          ${
            available.length > 0
              ? html`
                  <div style="margin-top: 8px;">
                    <select
                      class="fallback-add-select"
                      ?disabled=${disabled}
                      @change=${(e: Event) => {
                        const select = e.target as HTMLSelectElement;
                        const value = select.value;
                        if (value) {
                          onFallbackAdd(agent.id, value);
                          select.value = "";
                        }
                      }}
                    >
                      <option value="">+ ${t("agents.fallback_add")}</option>
                      ${available.map(
                        (entry) => html`
                          <option value=${entry.modelId}>
                            ${entry.alias ? `${entry.alias} (${entry.modelId})` : entry.modelId}
                          </option>
                        `,
                      )}
                    </select>
                  </div>
                `
              : nothing
          }
        </div>
      </div>

      <!-- Save/Reload buttons -->
      <div class="row" style="justify-content: flex-end; gap: 8px; margin-top: 16px;">
        <button class="btn btn--sm" ?disabled=${configLoading} @click=${onConfigReload}>
          ${t("agents.reload_config")}
        </button>
        <button
          class="btn btn--sm primary"
          ?disabled=${configSaving || !configDirty}
          @click=${onConfigSave}
        >
          ${configSaving ? t("common.saving") : t("common.save")}
        </button>
      </div>
    </section>
  `;
}

/** Resolve the current value for an env var from the config form. */
function resolveEnvVarValue(configForm: Record<string, unknown> | null, envVar: string): string {
  if (!configForm) return "";
  const env = configForm.env as { vars?: Record<string, string> } | undefined;
  return env?.vars?.[envVar] ?? "";
}

/** Set of well-known provider env var names for filtering custom vars. */
const KNOWN_ENV_KEYS = new Set(PROVIDER_API_KEYS.map((pk) => pk.envVar));

// ---------------------------------------------------------------------------
// Runtime Tab — Sub-Agent Status & Queue State
// ---------------------------------------------------------------------------

type AgentRuntimeProps = {
  subagentsStatus: SubagentsStatusResult | null;
  subagentsStatusLoading: boolean;
  queueStatus: QueueStatusResult | null;
  queueStatusLoading: boolean;
  onRefresh: () => void;
};

function formatRuntimeMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSec = seconds % 60;
  return remainingSec > 0 ? `${minutes}m ${remainingSec}s` : `${minutes}m`;
}

function runtimeStatusBadge(status: string) {
  const colorMap: Record<string, string> = {
    done: "var(--success, #34d399)",
    error: "var(--danger, #f87171)",
    aborted: "var(--warning, #fbbf24)",
    running: "var(--accent, #6c8cff)",
  };
  const color = colorMap[status] ?? "var(--text-muted, #888)";
  return html`<span style="
    display: inline-block;
    padding: 1px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
    background: ${color}20;
    color: ${color};
  ">${status}</span>`;
}

function renderAgentRuntime(props: AgentRuntimeProps) {
  const sa = props.subagentsStatus;
  const qs = props.queueStatus;

  return html`
    <section class="card" style="margin-bottom: 16px;">
      <div class="row" style="justify-content: space-between; align-items: center;">
        <div>
          <div class="card-title">${t("agents.runtime_title")}</div>
          <div class="card-sub">${t("agents.runtime_sub")}</div>
        </div>
        <button class="btn btn--sm" ?disabled=${props.subagentsStatusLoading} @click=${props.onRefresh}>
          ${props.subagentsStatusLoading ? t("common.loading") : t("common.refresh")}
        </button>
      </div>
    </section>

    <!-- Active Sub-Agents -->
    <section class="card" style="margin-bottom: 16px;">
      <div class="card-title" style="display: flex; align-items: center; gap: 8px;">
        ${t("agents.runtime_active")}
        ${
          sa && sa.totalActive > 0
            ? html`<span style="
              background: rgba(108,140,255,0.15);
              color: var(--accent, #6c8cff);
              font-size: 11px;
              font-weight: 600;
              padding: 1px 8px;
              border-radius: 10px;
            ">${sa.totalActive}</span>`
            : nothing
        }
      </div>

      ${
        props.subagentsStatusLoading && !sa
          ? html`<div class="muted" style="padding: 12px 0;">${t("common.loading")}</div>`
          : !sa || sa.active.length === 0
            ? html`<div class="muted" style="padding: 12px 0;">${t("agents.runtime_no_active")}</div>`
            : html`
              <div style="margin-top: 12px; display: flex; flex-direction: column; gap: 8px;">
                ${sa.active.map(
                  (run) => html`
                  <div style="
                    background: rgba(255,255,255,0.03);
                    border: 1px solid rgba(108,140,255,0.15);
                    border-radius: var(--radius-sm, 8px);
                    padding: 12px 16px;
                  ">
                    <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 6px;">
                      ${runtimeStatusBadge("running")}
                      <span style="font-weight: 600; font-size: 13px;">
                        ${run.label || run.runId.slice(0, 12)}
                      </span>
                      ${
                        run.model
                          ? html`<span style="
                        font-size: 11px;
                        color: var(--text-muted, #888);
                        font-family: monospace;
                      ">${run.model}</span>`
                          : nothing
                      }
                    </div>
                    <div style="font-size: 12px; color: var(--text-muted, #aaa); margin-bottom: 4px;">
                      ${run.task.length > 120 ? run.task.slice(0, 120) + "..." : run.task}
                    </div>
                    <div style="display: flex; gap: 12px; font-size: 11px; color: var(--text-muted, #888);">
                      <span>${t("agents.runtime_elapsed")}: ${formatRuntimeMs(run.runtimeMs)}</span>
                      <span style="font-family: monospace;">${run.childSessionKey.slice(0, 20)}</span>
                    </div>
                  </div>
                `,
                )}
              </div>
            `
      }
    </section>

    <!-- Queue State -->
    <section class="card" style="margin-bottom: 16px;">
      <div class="card-title">${t("agents.runtime_queue")}</div>
      ${
        props.queueStatusLoading && !qs
          ? html`<div class="muted" style="padding: 12px 0;">${t("common.loading")}</div>`
          : !qs
            ? html`<div class="muted" style="padding: 12px 0;">${t("agents.runtime_queue_unavailable")}</div>`
            : html`
              <div style="margin-top: 12px; display: flex; flex-wrap: wrap; gap: 12px;">
                ${Object.entries(qs.lanes).map(([name, lane]) => {
                  const utilPct =
                    lane.maxConcurrent > 0
                      ? Math.min(100, Math.round((lane.active / lane.maxConcurrent) * 100))
                      : 0;
                  const barColor =
                    lane.active > 0 && lane.depth > 0
                      ? "var(--warning, #fbbf24)"
                      : lane.active > 0
                        ? "var(--success, #34d399)"
                        : "var(--text-muted, #444)";
                  return html`
                    <div style="
                      flex: 1;
                      min-width: 140px;
                      background: rgba(255,255,255,0.03);
                      border: 1px solid rgba(255,255,255,0.06);
                      border-radius: var(--radius-sm, 8px);
                      padding: 10px 14px;
                    ">
                      <div style="font-family: monospace; font-size: 12px; font-weight: 600; margin-bottom: 6px;">${name}</div>
                      <div style="
                        height: 6px;
                        background: rgba(255,255,255,0.06);
                        border-radius: 3px;
                        overflow: hidden;
                        margin-bottom: 6px;
                      ">
                        <div style="
                          height: 100%;
                          width: ${utilPct}%;
                          background: ${barColor};
                          border-radius: 3px;
                          transition: width 0.3s;
                        "></div>
                      </div>
                      <div style="display: flex; justify-content: space-between; font-size: 11px; color: var(--text-muted, #888);">
                        <span>${lane.active}/${lane.maxConcurrent} ${t("agents.runtime_slots")}</span>
                        ${
                          lane.depth > 0
                            ? html`<span style="color: var(--warning, #fbbf24);">${lane.depth} ${t("dashboard.queue_queued")}</span>`
                            : nothing
                        }
                      </div>
                    </div>
                  `;
                })}
              </div>
            `
      }
    </section>

    <!-- Recent Completions -->
    <section class="card">
      <div class="card-title" style="display: flex; align-items: center; gap: 8px;">
        ${t("agents.runtime_recent")}
        ${
          sa && sa.totalRecent > 0
            ? html`<span style="
              background: rgba(255,255,255,0.08);
              color: var(--text-muted, #888);
              font-size: 11px;
              font-weight: 600;
              padding: 1px 8px;
              border-radius: 10px;
            ">${sa.totalRecent}</span>`
            : nothing
        }
      </div>
      <div class="card-sub">${t("agents.runtime_recent_sub")}</div>

      ${
        !sa || sa.recent.length === 0
          ? html`<div class="muted" style="padding: 12px 0;">${t("agents.runtime_no_recent")}</div>`
          : html`
            <div style="margin-top: 12px; overflow-x: auto;">
              <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
                <thead>
                  <tr style="border-bottom: 1px solid rgba(255,255,255,0.08);">
                    <th style="text-align: left; padding: 6px 12px; color: var(--text-muted, #888); font-weight: 500; font-size: 12px;">${t("agents.runtime_col_label")}</th>
                    <th style="text-align: left; padding: 6px 12px; color: var(--text-muted, #888); font-weight: 500; font-size: 12px;">${t("agents.runtime_col_status")}</th>
                    <th style="text-align: left; padding: 6px 12px; color: var(--text-muted, #888); font-weight: 500; font-size: 12px;">${t("agents.runtime_col_model")}</th>
                    <th style="text-align: right; padding: 6px 12px; color: var(--text-muted, #888); font-weight: 500; font-size: 12px;">${t("agents.runtime_col_runtime")}</th>
                  </tr>
                </thead>
                <tbody>
                  ${sa.recent.map(
                    (run) => html`
                    <tr style="border-bottom: 1px solid rgba(255,255,255,0.04);">
                      <td style="padding: 8px 12px;">
                        <div style="font-weight: 500;">${run.label || run.runId.slice(0, 12)}</div>
                        <div style="font-size: 11px; color: var(--text-muted, #888); max-width: 300px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                          ${run.task}
                        </div>
                      </td>
                      <td style="padding: 8px 12px;">
                        ${runtimeStatusBadge(run.status)}
                      </td>
                      <td style="padding: 8px 12px; font-family: monospace; font-size: 12px; color: var(--text-muted, #888);">
                        ${run.model ?? "-"}
                      </td>
                      <td style="padding: 8px 12px; text-align: right; font-family: monospace; font-size: 12px;">
                        ${formatRuntimeMs(run.runtimeMs)}
                      </td>
                    </tr>
                  `,
                  )}
                </tbody>
              </table>
            </div>
          `
      }

      ${
        sa
          ? html`
        <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid rgba(255,255,255,0.06); display: flex; gap: 16px; font-size: 11px; color: var(--text-muted, #666);">
          <span>${t("agents.runtime_watchdog")}: ${sa.watchdog.running ? t("agents.runtime_watchdog_on") : t("agents.runtime_watchdog_off")}</span>
          <span>${t("agents.runtime_scans")}: ${sa.watchdog.totalScans}</span>
          <span>${t("agents.runtime_aborted")}: ${sa.watchdog.totalAborted}</span>
          <span>${t("agents.runtime_registered")}: ${sa.totalRegistered}</span>
        </div>
      `
          : nothing
      }
    </section>
  `;
}
